import React from 'react';
import { EditIcon, CheckCircle2Icon, XCircleIcon, CalendarIcon, HashIcon, Trash2Icon, UserIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export interface Invoice {
  id: string;
  supplier: string;
  invoiceNumber: string;
  date: string;
  category: 'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet';
  type: 'Ladeboks' | 'Andet';
  amount: number;
  paid: boolean;
  customerId?: string;
  customerName?: string;
  notes?: string;
}
interface InvoiceCardProps {
  invoice: Invoice;
  index: number;
  onEdit: (invoice: Invoice) => void;
  onDelete: (invoice: Invoice) => void;
  readOnly?: boolean;
}
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'Sikkerhed':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'Værktøj':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'Materialer':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'Andet':
      return 'bg-stone-100 text-stone-700 border-stone-200';
    default:
      return 'bg-stone-100 text-stone-700 border-stone-200';
  }
};
const getTypeColor = (type: string) => {
  switch (type) {
    case 'Ladeboks':
      return 'bg-purple-100 text-purple-700 border-purple-200';
    case 'Andet':
      return 'bg-stone-100 text-stone-700 border-stone-200';
    default:
      return 'bg-stone-100 text-stone-700 border-stone-200';
  }
};
export function InvoiceCard({
  invoice,
  index,
  onEdit,
  onDelete,
  readOnly = false
}: InvoiceCardProps) {
  return <motion.article initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    delay: index * 0.05
  }} className="bg-white rounded-xl p-5 shadow-sm border border-stone-100 hover:shadow-md transition-shadow duration-200" data-id="element-855">
      <div className="flex items-start justify-between mb-3" data-id="element-856">
        <div className="flex-1" data-id="element-857">
          <h3 className="text-lg font-semibold text-stone-900 mb-1" data-id="element-858">
            {invoice.supplier}
          </h3>
          <div className="flex items-center gap-2 text-sm text-stone-500" data-id="element-859">
            <HashIcon className="w-3.5 h-3.5" data-id="element-860" />
            <span data-id="element-861">{invoice.invoiceNumber}</span>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-862">
          {!readOnly && <>
              <button onClick={() => onEdit(invoice)} className="p-1.5 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all" aria-label="Rediger faktura" data-id="element-863">
                <EditIcon className="w-4 h-4" data-id="element-864" />
              </button>
              <button onClick={() => onDelete(invoice)} className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" aria-label="Slet faktura" data-id="element-865">
                <Trash2Icon className="w-4 h-4" data-id="element-866" />
              </button>
            </>}
          {invoice.paid ? <div className="p-1.5 bg-emerald-50 rounded-lg" data-id="element-867">
              <CheckCircle2Icon className="w-4 h-4 text-emerald-600" data-id="element-868" />
            </div> : <div className="p-1.5 bg-red-50 rounded-lg" data-id="element-869">
              <XCircleIcon className="w-4 h-4 text-red-600" data-id="element-870" />
            </div>}
        </div>
      </div>

      <div className="space-y-2 mb-4" data-id="element-871">
        <div className="flex items-center gap-2 text-sm" data-id="element-872">
          <CalendarIcon className="w-4 h-4 text-teal-600" data-id="element-873" />
          <span className="text-stone-500" data-id="element-874">Dato:</span>
          <span className="text-stone-900 font-medium" data-id="element-875">
            {new Date(invoice.date).toLocaleDateString('da-DK')}
          </span>
        </div>

        {invoice.customerName && <div className="flex items-center gap-2 text-sm" data-id="element-876">
            <UserIcon className="w-4 h-4 text-blue-600" data-id="element-877" />
            <span className="text-stone-500" data-id="element-878">Kunde:</span>
            <span className="text-stone-900 font-medium" data-id="element-879">
              {invoice.customerName}
            </span>
          </div>}

        <div className="flex items-center gap-2 flex-wrap" data-id="element-880">
          <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getTypeColor(invoice.type)}`} data-id="element-881">
            {invoice.type}
          </span>
          <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getCategoryColor(invoice.category)}`} data-id="element-882">
            {invoice.category}
          </span>
          <span className={`px-3 py-1 text-xs font-medium rounded-full border ${invoice.paid ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}`} data-id="element-883">
            {invoice.paid ? 'Betalt' : 'Ubetalt'}
          </span>
        </div>
      </div>

      {invoice.notes && <div className="text-sm text-stone-600 mb-4 line-clamp-2" dangerouslySetInnerHTML={{
      __html: invoice.notes
    }} data-id="element-884" />}

      <div className="pt-4 border-t border-stone-100" data-id="element-885">
        <div className="flex items-center justify-between" data-id="element-886">
          <span className="text-sm text-stone-500" data-id="element-887">Beløb</span>
          <span className="text-xl font-bold text-stone-900" data-id="element-888">
            {invoice.amount.toLocaleString('da-DK')} kr
          </span>
        </div>
      </div>
    </motion.article>;
}